import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomerTestsTypes } from '@enums/customer-tests-types';
import { FulfillmentMethod } from '@enums/fulfillment-method';
import { Gender } from '@enums/gender';
import { environment } from '@environments/environment';
import { ApiOrderRequest } from '@interfaces/api-order-request';
import { LoadCartOptions } from '@interfaces/load-cart-options';
import { InHomeCollectionService, ResultService } from '@Medology/ng-findalab';
import { Payment } from '@medology/ngx-payment';
import { Address } from '@models/address';
import { AnalyteCareLoadCartResponse } from '@models/analyte-care-load-cart-response';
import { CartAdditionalItem } from '@models/cart-additional-item';
import { ConsultableOrderRequest } from '@models/consultable-order-request';
import { TreatmentConsultationRequest } from '@models/consultation-request/treatment-consultation-request';
import { OrderConsultLoadCartRequest } from '@models/order-consult-load-cart-request';
import { OrderRequest } from '@models/order-request';
import { OrderResponse } from '@models/order-response';
import { Reorder } from '@models/reorder';
import { Test } from '@models/test';
import { TreatmentType } from '@models/treatment-types/treatment-type.model';
import { AuthorizationService } from '@services/authorization.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DataLayerService } from '@services/data-layer.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { FormService } from '@services/form.service';
import { GraphqlService } from '@services/graphql.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';
import { TreatmentTypeService } from '@services/treatment-type.service';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OrderConsultationRequestService extends OrderService {
  protected document = inject<Document>(DOCUMENT);
  protected http = inject(HttpClient);
  protected router = inject(Router);
  protected storage = inject(StorageService);
  protected results = inject(ResultService);
  protected formService = inject(FormService);
  protected authService = inject(AuthorizationService);
  protected inHomeService = inject(InHomeCollectionService);
  protected dataLayerService = inject(DataLayerService);
  protected consultationStorageService = inject(ConsultationStorageService);
  protected errorLoggingService = inject(ErrorLoggingService);
  protected treatmentTypeService = inject(TreatmentTypeService);
  protected graphqlService = inject(GraphqlService);
  protected navigationService = inject(NavigationService);

  public constructor() {
    super();
  }

  /**
   * Gets the coupon code from the storage.
   */
  public override get couponCode(): string | null {
    return this.storage.coupon?.coupon_code || null;
  }

  /**
   * Get the order submit validation errors.
   */
  public override getOrderSubmitValidationErrors(): string[] {
    const errors: string[] = super.getOrderSubmitValidationErrors();
    const treatmentType = this.treatmentTypeService.getTreatmentType(this.storage.treatmentType);
    const state = this.checkoutForm.value?.address?.state;

    const testIds = this.storage.tests.map((test) => test.id);

    if (treatmentType && state && treatmentType.isStateRestricted(state, testIds)) {
      errors.push(treatmentType.restrictedStatesError);
    }

    return errors;
  }

  /**
   * Avoids the order completed request to be sent.
   */
  public override orderCompleted(): Observable<any> {
    return of({});
  }

  /** {@inheritDoc OrderService} */
  public override listenForLabSelection(): Subscription {
    return of({}).subscribe();
  }

  /**
   * Load cart is not needed for consultation-based sites.
   *
   * These sites own the entire ordering workflow and don't require the API validation step that other platforms need. All
   * order data is maintained in localStorage and sent directly to the order placement endpoint.
   *
   * However, if a coupon exists in storage, we fetch and validate it from the API.
   *
   * @param _tests unused parameter, kept for interface compatibility
   * @param _options unused parameter, kept for interface compatibility
   *
   * @returns {Observable<null>} null observable since no cart loading is performed
   */
  public override loadCart(_tests?: Test[], _options?: LoadCartOptions): Observable<null> {
    this.storage.testsSubject.next(this.storage.tests);

    const couponCode = this.storage.coupon?.coupon_code;

    if (!couponCode) {
      return of(null);
    }

    return this.validateCoupon(couponCode);
  }

  /** {@inheritDoc OrderService} */
  public override parseCartResponse(response: AnalyteCareLoadCartResponse): Observable<AnalyteCareLoadCartResponse> {
    return this.parseCoupon(response);
  }

  /**
   * Submits order and marks address as complete in storage upon success.
   *
   * @param {string} deviceData device data for order submission
   * @param {Payment[]} payments the payment data from the payment package
   * @param {boolean} isExternalSubscriptionOrder whether the order is billed through the external subscription
   * service, submitting all payments in a single request
   *
   * @returns {Observable<OrderResponse>} observable containing the order response
   */
  public override submitOrder(
    deviceData: string,
    payments: Payment[],
    isExternalSubscriptionOrder: boolean = false,
  ): Observable<OrderResponse> {
    return super
      .submitOrder(deviceData, payments, isExternalSubscriptionOrder)
      .pipe(tap(() => (this.storage.addressComplete = true)));
  }

  /**
   * Returns the default lab ID from configuration.
   *
   * @returns {number} the default order lab ID
   */
  public override getOrderLabId(): number {
    return +environment.defaultOrderLabId;
  }

  /**
   * Creates a LoadCartRequest from a Reorder object.
   *
   * @param {Reorder} reorder the Reorder object containing the details of the order to reorder
   */
  public override getOrderRequestFromReorder(reorder: Reorder): OrderConsultLoadCartRequest {
    // TODO: https://github.com/Medology/ng-checkout/issues/3813
    // Current issue: Coupon is embedded in the test name, e.g. "Standard UTI coupon (COUPONCODE)"
    const coupon = reorder.tests
      .find((customerTest) => customerTest.customer_tests_type === CustomerTestsTypes.Discount)
      ?.customer_tests_name.match(/\(([^)]+)\)/);

    return new OrderConsultLoadCartRequest({
      tests: reorder.tests
        .filter((customerTest) => customerTest.is_test_or_panel)
        .map((customerTest) => customerTest.test),
      coupon: coupon ? coupon[1] : null,
    });
  }

  /** {@inheritDoc OrderService.getCartAdditionalItems} */
  public override getCartAdditionalItems(): CartAdditionalItem[] {
    const treatmentType = this.storage.treatmentType;
    const isShipped = this.storage.isShipped;

    if (!treatmentType) {
      return [
        {
          description: `Pharmacy ${isShipped ? FulfillmentMethod.Delivery : FulfillmentMethod.Pickup}`,
          price: '$0.00',
        },
      ];
    }

    return this.treatmentTypeService.getTreatmentType(treatmentType).getCartAdditionalItems(isShipped);
  }

  /** {@inheritDoc OrderService} */
  protected override getTotalDiscount(): number {
    return 0;
  }

  /**
   * Guardian info feature is not enabled for sites using this service.
   *
   * @param {OrderRequest} _orderRequest the order request object
   */
  protected override addGuardianInfoToOrderRequest(_orderRequest: OrderRequest): void {
    return;
  }

  /**
   * In-home collection booking feature is not enabled for sites using this service.
   *
   * @param {OrderRequest} _orderRequest the order request object
   */
  protected override addInHomeCollectionBookingToOrderRequest(_orderRequest: OrderRequest): void {
    return;
  }

  /**
   * Returns address data based on whether order is shipped or uses center address.
   *
   * @param {Address} addressFormValues address values from form
   *
   * @returns {Partial<OrderRequest>} address fields for the order request
   */
  protected override getOrderFullAddress(addressFormValues: Address): Partial<OrderRequest> {
    return this.storage.isShipped
      ? {
          address: addressFormValues.streetAddress,
          address2: addressFormValues.streetAddress2,
          city: addressFormValues.city,
          state: addressFormValues.state,
          zip: addressFormValues.zipcode,
        }
      : {
          address: this.storage.center.address,
          city: this.storage.center.city,
          state: this.storage.center.state,
          zip: this.storage.center.zip_code,
        };
  }

  /**
   * Creates an order request object, optionally enhanced with consultation data for treatment types.
   *
   * @param {string} deviceData device data from Braintree
   * @param {Payment[]} payments the payment data from the payment package
   * @param {boolean} isExternalSubscriptionOrder whether the order is billed through the external subscription
   * service, submitting all payments in a single request
   *
   * @returns {ApiOrderRequest} the order request object
   */
  protected override getOrderRequest(
    deviceData: string,
    payments: Payment[],
    isExternalSubscriptionOrder: boolean = false,
  ): ApiOrderRequest {
    const orderRequest = super.getOrderRequest(deviceData, payments, isExternalSubscriptionOrder) as OrderRequest;
    const planId = this.storage.tests.find((test) => !!test.plan_id)?.plan_id;

    if (planId) {
      orderRequest.plan_id = planId;
    }

    // For now, we have the assumption that orders without a treatment type are non-consultable on sites that use this
    // specific service. Therefore, we place the order with only the order payload.
    if (!this.storage.treatmentType) {
      return orderRequest;
    }

    const treatmentType = this.treatmentTypeService.getTreatmentType(this.storage.treatmentType);
    const consultationRequest = this.getTreatmentConsultationRequest(treatmentType, orderRequest.gender);

    orderRequest.tests = (orderRequest.tests as number[]).map(
      (testId) => treatmentType.getMedicationByGender(orderRequest.gender, testId) || testId,
    );

    return new ConsultableOrderRequest(orderRequest, consultationRequest);
  }

  /**
   * {@inheritDoc}
   */
  protected override mapResponseToReorder(response: any): Reorder {
    const consultationRequest = response.consultation_requests[0];

    return {
      ...super.mapResponseToReorder(response),
      tests: response.tests,
      coupon_code: null,
      center: JSON.parse(consultationRequest.pharmacy),
      consultation: {
        allergies: consultationRequest.allergies,
        prescriptions: consultationRequest.prescriptions,
        health_info: consultationRequest.health_info,
        treatment_type: consultationRequest.treatment.id,
      },
    };
  }

  /**
   * Gets the extra charge object for the order request.
   * For consultation-based orders, extra charges are not supported, so this returns an empty object.
   */
  protected override getExtraChargeObject(): {} {
    return {};
  }

  /**
   * Redirects to the next page after order completion.
   */
  protected override redirectToNextPage(): void {
    this.router.navigateByUrl(this.navigationService.getGenderBasedUpsellUrl());
  }

  /**
   * Validates and loads the coupon from the API.
   *
   * @param {string} couponCode the coupon code to validate
   *
   * @returns {Observable<null>} null observable after coupon validation
   */
  private validateCoupon(couponCode: string): Observable<null> {
    return this.getCoupon(couponCode).pipe(
      tap((coupon) => (this.storage.coupon = coupon)),
      map(() => null),
      catchError((error) => {
        this.errorLoggingService.logHttpError(error, 'Failed to validate coupon', { couponCode });

        return of(null);
      }),
    );
  }

  /**
   * Creates a TreatmentConsultationRequest.
   *
   * @param {TreatmentType} treatmentType the type of treatment for the consultation
   * @param {Gender} gender the patient's gender
   *
   * @returns {TreatmentConsultationRequest} the consultation request to be sent
   */
  private getTreatmentConsultationRequest(treatmentType: TreatmentType, gender: Gender): TreatmentConsultationRequest {
    return new TreatmentConsultationRequest(
      this.formService.checkout.get('patient') as FormGroup,
      this.formService.checkout.get('medicalHistory') as FormGroup,
      this.storage.isShipped ? treatmentType?.designatedPharmacyId : this.storage.center.id,
      treatmentType.getTreatmentNameByGender(gender),
      treatmentType.customMedicalHistoryQuestions,
      treatmentType.defaultConsultationStatus,
    );
  }
}
